import type {
  ChatRequest,
  ChatResponse,
  FeedbackRequest,
  SearchRequest,
  SearchResponse,
  HealthResponse,
  RipgrepResponse,
} from '../types';
import { API_BASE, getAuthHeaders, fetchApi } from './apiClient';

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new ApiError(
      data?.detail || `HTTP error ${response.status}`,
      response.status,
      data
    );
  }
  return response.json();
}

export const api = {
  /**
   * Send a chat query and get AI response
   */
  async chat(
    request: ChatRequest,
    signal?: AbortSignal
  ): Promise<ChatResponse> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal,
    });
    return handleResponse<ChatResponse>(response);
  },

  /**
   * Search documents directly without LLM
   */
  async search(
    request: SearchRequest,
    signal?: AbortSignal
  ): Promise<SearchResponse> {
    const params = new URLSearchParams();
    params.set('q', request.q);
    if (request.top_k) params.set('top_k', request.top_k.toString());
    if (request.doc_type) params.set('doc_type', request.doc_type);
    if (request.category) params.set('category', request.category);

    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/search?${params}`, {
      method: 'GET',
      headers,
      signal,
    });
    return handleResponse<SearchResponse>(response);
  },

  /**
   * Submit feedback for a response
   */
  async submitFeedback(request: FeedbackRequest): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/feedback`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new ApiError(
        data?.detail || 'Failed to submit feedback',
        response.status,
        data
      );
    }
  },

  /**
   * Ripgrep-style text search across ingested document chunks.
   */
  async ripgrep(
    query: string,
    options?: { category?: string; caseSensitive?: boolean; maxResults?: number },
    signal?: AbortSignal,
  ): Promise<RipgrepResponse> {
    const params = new URLSearchParams();
    params.set('q', query);
    if (options?.category) params.set('category', options.category);
    if (options?.caseSensitive) params.set('case_sensitive', 'true');
    if (options?.maxResults) params.set('max_results', options.maxResults.toString());

    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/ripgrep?${params}`, {
      method: 'GET',
      headers,
      signal,
    });
    return handleResponse<RipgrepResponse>(response);
  },

  /**
   * Send a query directly to ERAX (Text-to-SQL) for testing/debugging.
   * Bypasses the intent router — calls /erax/ask directly.
   */
  async eraxAsk(
    query: string,
    options?: { includeChart?: boolean },
    signal?: AbortSignal,
  ): Promise<Record<string, unknown>> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/erax/ask`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query,
        include_chart: options?.includeChart ?? true,
      }),
      signal,
    });
    return handleResponse<Record<string, unknown>>(response);
  },

  /**
   * Check API health status
   */
  async health(): Promise<HealthResponse> {
    const response = await fetchApi(`${API_BASE}/health`);
    return handleResponse<HealthResponse>(response);
  },
};

export { ApiError };
